import React, { useMemo } from 'react';
import CatalogImage from '../../catalog/CatalogImage';
import { formatProductTitle } from '../../formatProductTitle';
import InstagramEmbed from '../InstagramEmbed';
import ProductShelfSection from '../ProductShelfSection';
import { HOME_COPY_DEFAULTS } from '../../content/pageCopyDefaults';
import { useInstagramFeed } from '../../instagram/useInstagramFeed';
import { logSocialClick } from '../../storefront/storefrontEvents';

function InstagramTileLink({ href, ariaLabel, onClick, children }) {
  if (!href) {
    return (
      <div className="home-instagram-tile" aria-label={ariaLabel}>
        {children}
      </div>
    );
  }
  return (
    <a
      className="home-instagram-tile"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {children}
    </a>
  );
}

/** Instagram / social proof block on the home page. */
export default function HomeInstagramSection({ site, homeCopy = {}, allProducts = [] }) {
  const embedHtml = String(site.instagram_embed_html || '').trim();
  const instagramUrl = String(site.instagram_url || '').trim();
  const { posts: instagramPosts, loading: instagramLoading } = useInstagramFeed({
    limit: 6,
    enabled: !embedHtml,
  });

  const fallbackPosts = useMemo(() => {
    const seenImages = new Set();
    const out = [];
    for (const p of allProducts || []) {
      const image = String(p?.image || '').trim();
      if (!image || seenImages.has(image)) continue;
      seenImages.add(image);
      out.push({
        key: `${p.category}-${p.id}`,
        image,
        caption: formatProductTitle(p.name || ''),
        permalink: '',
      });
      if (out.length >= 6) break;
    }
    return out;
  }, [allProducts]);

  const displayPosts = useMemo(() => {
    if (embedHtml) return [];
    if (instagramPosts.length > 0) {
      return instagramPosts.map((post) => ({
        key: post.id,
        image: post.image,
        caption: post.caption,
        permalink: post.permalink,
        external: true,
      }));
    }
    if (instagramLoading) return [];
    return fallbackPosts.map((post) => ({ ...post, external: false }));
  }, [embedHtml, fallbackPosts, instagramLoading, instagramPosts]);

  const buttonLabel = homeCopy.instagram_button || HOME_COPY_DEFAULTS.instagram_button;
  const useScrollTiles = !embedHtml && displayPosts.length > 0;
  const hasContent = Boolean(embedHtml) || displayPosts.length > 0;

  if (!hasContent) return null;

  return (
    <ProductShelfSection
      eyebrow=""
      title={homeCopy.instagram_title || 'Gallery'}
      actionHref={instagramUrl || undefined}
      actionLabel={instagramUrl ? buttonLabel : ''}
      layout={useScrollTiles ? 'scroll' : 'grid'}
      tone="default"
      className="home-instagram-shelf"
      ariaLabel="Instagram gallery"
    >
      {embedHtml ? (
        <div className="home-instagram-shelf__embed product-shelf__cell product-shelf__cell--wide">
          <InstagramEmbed html={embedHtml} />
        </div>
      ) : (
        displayPosts.map((item) => {
          const targetUrl = item.permalink || instagramUrl;
          const ariaLabel = item.caption
            ? `View ${item.caption} on Instagram`
            : 'View on Instagram';
          return (
            <div className="product-shelf__cell" key={item.key}>
              <InstagramTileLink
                href={targetUrl || undefined}
                ariaLabel={ariaLabel}
                onClick={targetUrl ? () => logSocialClick({ platform: 'instagram', targetUrl }) : undefined}
              >
                {item.external ? (
                  <img
                    src={item.image}
                    alt={item.caption || 'Instagram post'}
                    className="home-instagram-tile__img"
                    loading="lazy"
                    decoding="async"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <CatalogImage src={item.image} alt={item.caption} className="home-instagram-tile__img" />
                )}
                <span className="home-instagram-tile__overlay" aria-hidden>
                  <span className="home-instagram-tile__icon">↗</span>
                </span>
              </InstagramTileLink>
            </div>
          );
        })
      )}
    </ProductShelfSection>
  );
}
